import type { Actor, Incident, PulseRate, Target } from '@/types/map'
import {
  clockUTC,
  incidentAttributionLabel,
  incidentAttributionQualifier,
  incidentStatusVar,
  severityVar,
} from '../map-display-utils'
import { ProvenanceMiniLine, ReasonChips } from '../ProjectionProvenance'
import '../map-console.css'

export interface IncidentTickerProps {
  incidents: Incident[]
  actors: Actor[]
  targets: Target[]
  onIncidentClick: (incidentId: string) => void
  pulseRate: PulseRate
}

export function IncidentTicker({
  incidents,
  actors,
  targets,
  onIncidentClick,
  pulseRate,
}: IncidentTickerProps) {
  const byActor = new Map(actors.map((a) => [a.id, a.name]))
  const byTarget = new Map(targets.map((t) => [t.id, t.name]))
  const rows = [...incidents].sort((a, b) => b.startedAt - a.startedAt).slice(0, 200)
  const live = pulseRate !== 'off'

  return (
    <section className="soc-panel flex h-full flex-col overflow-hidden">
      <header className="soc-panel-head flex shrink-0 items-center justify-between px-3 py-2">
        <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          <span className="text-[color:var(--interactive-primary)]" aria-hidden>
            ▸
          </span>
          Live Feed
        </h3>
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)]">
          <span
            className={`h-1.5 w-1.5 rounded-full ${live ? 'soc-stream-dot' : ''}`}
            style={{
              background: live ? 'var(--severity-critical)' : 'var(--text-tertiary)',
            }}
            aria-hidden
          />
          {live ? 'Streaming' : 'Paused'}
        </span>
      </header>
      {!live && (
        <p
          role="status"
          className="mx-2 mt-2 rounded bg-[color:var(--surface-tertiary)] px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)]"
        >
          Live feed paused
        </p>
      )}
      {rows.length === 0 ? (
        <p className="px-3 py-4 text-xs text-[color:var(--text-tertiary)]">
          No incidents in range
        </p>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {rows.map((inc) => {
            const actorName = incidentAttributionLabel(inc, byActor.get(inc.actorId ?? ''))
            const attributionQualifier = incidentAttributionQualifier(inc)
            const targetName = byTarget.get(inc.targetId) ?? inc.targetId
            const technique = inc.mitreTechniques[0]
            const cve = inc.cveIds[0]
            return (
              <li
                key={inc.id}
                data-testid="ticker-row"
                onClick={() => onIncidentClick(inc.id)}
                className="flex cursor-pointer flex-col gap-0.5 border-b border-[color:var(--border-subtle)] px-3 py-2 text-xs transition-colors hover:bg-[color:var(--surface-tertiary)]"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: severityVar(inc.severity) }}
                    aria-hidden
                  />
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.12em]"
                    style={{ color: severityVar(inc.severity) }}
                  >
                    {inc.severity}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-[color:var(--text-tertiary)]">
                    {clockUTC(inc.startedAt)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[color:var(--text-primary)]">{actorName}</span>
                  <span className="text-[color:var(--text-tertiary)]" aria-hidden>
                    →
                  </span>
                  <span className="truncate text-[color:var(--text-secondary)]">
                    {targetName}
                  </span>
                </div>
                {attributionQualifier && (
                  <div className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                    {attributionQualifier}
                  </div>
                )}
                <ReasonChips reasons={inc.unknownReasons} max={2} label={`Reasons for ${inc.id}`} />
                {inc.provenance && inc.provenance.length > 0 && (
                  <div className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                    <ProvenanceMiniLine provenance={inc.provenance} />
                  </div>
                )}
                {(technique || cve) && (
                  <div className="truncate font-mono text-[10px] text-[color:var(--text-tertiary)]">
                    {[technique, cve].filter(Boolean).join(' · ')}
                  </div>
                )}
                <span
                  className="font-mono text-[9px] uppercase tracking-[0.12em]"
                  style={{ color: incidentStatusVar(inc.status) }}
                >
                  {inc.status}
                </span>
                <span className="sr-only">{inc.id}</span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
